import React, { useCallback } from "react";
import { Button, Modal, Tooltip } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  SaveOutlined,
  CloseSquareOutlined,
} from "@ant-design/icons";
import ee from "services/EventEmitter";
import { MODAL } from "constants/translates";
import {
  DELETE_CONTRACTS_EVENT,
  REMOVE_CONTRACTS_EDITING_KEY,
  SAVE_CONTRACTS_ENTITY,
  SET_CONTRACTS_EDITING_KEY,
  UPDATE_CONTRACTS_ENTITY,
} from "constants/events";
import { observer } from "mobx-react";
import { Moment } from "moment";
import { UploadFile } from "antd/lib/upload/interface";
import { Contracts } from "models/Contracts";
import ContractsStore from "stores/ContractsStore";

interface Props {
  entity: Contracts;
  isTemporary?: boolean;
  form?: any;
}

interface FileProps {
  file: UploadFile;
  fileList: UploadFile[];
}

const Actions: React.FC<Props> = ({ entity, form, isTemporary }) => {
  const entityId = entity?.id;
  const { editingKeys } = ContractsStore;
  const editMode = editingKeys?.some((key) => key === entityId);

  const onDeleteHandler = useCallback(() => {
    Modal.confirm({
      title: MODAL.confirm_title,
      content: MODAL.confirm_delete,
      onOk: () => {
        ee.emit(DELETE_CONTRACTS_EVENT, {
          entityId,
          isTemporary,
        });
      },
    });
  }, [entityId]);

  const setEditMode = () => {
    ee.emit(SET_CONTRACTS_EDITING_KEY, entityId);
  };

  const saveRow = async () => {
    const row = await form.validateFields();

    const savingKeys = Object.keys(row).filter((current) => {
      return current.includes(`_${entityId}`);
    });

    const rowData = Object.fromEntries(
      Object.entries(row).filter((m) => savingKeys.includes(m[0]))
    );

    Object.keys(rowData).forEach(function (key) {
      const newKey = key?.replace(`_${entityId}`, "");
      if (rowData) {
        Object.defineProperty(
          rowData,
          newKey,
          Object.getOwnPropertyDescriptor(rowData, key) || ""
        );

        delete rowData[key];
      }
    });

    const rowAttachedData = rowData?.documents as FileProps;
    const rowFiles = rowAttachedData?.fileList?.map(
      (file: UploadFile) => file.originFileObj
    );

    const savingData = {
      ...rowData,
      created_date:
        (rowData.created_date as Moment)?.format("YYYY-MM-DD") || "",
      documents: entity?.documents,
    };

    const dateAction = entity.temporary
      ? SAVE_CONTRACTS_ENTITY
      : UPDATE_CONTRACTS_ENTITY;

    ee.emit(dateAction, { savingData, entityId, documents: rowFiles });
  };

  const cancelEdit = async () => {
    ee.emit(REMOVE_CONTRACTS_EDITING_KEY, entityId);
  };

  return (
    <>
      {editMode ? (
        <Tooltip placement="top" title="Save row">
          <Button type="primary" onClick={saveRow}>
            <SaveOutlined style={{ fontSize: "25px", paddingTop: "4px" }} />
          </Button>
        </Tooltip>
      ) : (
        <Tooltip placement="top" title="Edit row">
          <Button type="primary" onClick={setEditMode}>
            <EditOutlined />
          </Button>
        </Tooltip>
      )}
      <Tooltip placement="top" title="Delete row">
        <Button type="primary" onClick={onDeleteHandler}>
          <DeleteOutlined />
        </Button>
      </Tooltip>
      {editMode && (
        <>
          &nbsp;&nbsp;
          <Tooltip placement="top" title="Cancel changes">
            <Button type="primary" onClick={cancelEdit}>
              <CloseSquareOutlined />
            </Button>
          </Tooltip>
        </>
      )}
    </>
  );
};

export default observer(Actions);
